import { Map } from 'immutable';
import { createSelector } from 'reselect';
import { ASSET_STATUSES_KEY, AssetStatus } from './reducers';
import { getAssetQueue } from '../viewstates/selectors';
import { keyForAsset } from './assets';
import { getFeatureFlag } from '../feature-flags/selectors';
import {
    FEATURE_FLAG_ASSET_SERVER_OVERRIDE,
    FEATURE_FLAG_STATIC_ASSET_SERVER_OVERRIDE,
} from '../feature-flags';
import { isTethering, getTetheringAssetURL } from '../tethering/selectors';

const DEFAULT_CONTENT_ASSETS_URL = 'https://assets.sparxhomework.uk';
const DEFAULT_STATIC_ASSETS_URL = 'https://static.sparxhomework.uk/assets/algenie-web';

const getStateForAsset = (state, assetId, assetType, preload) =>
    state.getIn([ASSET_STATUSES_KEY, keyForAsset(assetId, assetType, preload)], Map());

const getStatusForAsset = (state, assetId, assetType, preload = false) =>
    getStateForAsset(state, assetId, assetType, preload).get('status');

/**
 * Returns true if we have successfully loaded the asset.
 */
export const isAssetLoaded = (state, assetId, assetType, preload = false) =>
    getStatusForAsset(state, assetId, assetType, preload) === AssetStatus.LOADED;

/**
 * Returns true if we have given up trying to load the asset.
 */
export const didAssetError = (state, assetId, assetType, preload = false) =>
    getStatusForAsset(state, assetId, assetType, preload) === AssetStatus.NOT_LOADED;

/**
 * Returns the current load attempt number for an asset.
 *      e.g. 1 if it is the first time loading an image
 */
export const getAssetLoadAttempt = (state, assetId, assetType, preload = false) =>
    getStateForAsset(state, assetId, assetType, preload).get('currentAttempt', 1);

/**
 * Returns the number of unsuccessful load attempts for an asset.
 */
export const getAssetFailedLoadAttempts = (state, assetId, assetType, preload) =>
    getStateForAsset(state, assetId, assetType, preload).get('failedAttempts', 0);

const getAssetStatuses = state => state.get(ASSET_STATUSES_KEY);

/**
 * Returns the next asset to be preloaded in the format {assetId, assetType}.
 */
export const getAssetToPreload = createSelector(
    [getAssetStatuses, getAssetQueue],
    (statuses, queue) => {
        const notLoaded = ({ assetId, assetType }) => {
            const state = statuses.get(keyForAsset(assetId, assetType, true), Map());
            const status = state.get('status');
            // Try to preload assets that haven't already loaded and haven't
            // already failed to load.
            return status !== AssetStatus.LOADED && status !== AssetStatus.NOT_LOADED;
        };

        for (const queueItem of queue) {
            if (notLoaded(queueItem)) {
                return queueItem;
            }
        }

        return undefined;
    },
);

/**
 * Gets the URL for downloading content assets (i.e. skill images and videos). This will be the
 * default URL unless overridden with the "asset-server-override" feature flag
 * @param state the Redux state
 * @return {string}
 */
export const getContentAssetsURL = state => {
    const overrideURL = getFeatureFlag(state, FEATURE_FLAG_ASSET_SERVER_OVERRIDE);
    if (overrideURL) {
        return overrideURL;
    }
    if (isTethering(state)) {
        return getTetheringAssetURL(state);
    }
    return DEFAULT_CONTENT_ASSETS_URL;
};

/**
 * Gets the URL for downloading static assets (i.e. tutorial videos and Pixi.js games assets).
 * This will be the default URL unless overridden with the "static-asset-server-override" feature
 * flag
 * @param state the Redux state
 * @return {string}
 */
export const getStaticAssetsURL = state => {
    const overrideURL = getFeatureFlag(state, FEATURE_FLAG_STATIC_ASSET_SERVER_OVERRIDE);
    return overrideURL || DEFAULT_STATIC_ASSETS_URL;
};

/**
 * Creates a selector to get the URL for a single asset
 * @param assetId the asset to fetch
 */
export const createContentURLSelector = assetId =>
    createSelector(getContentAssetsURL, baseURL => {
        // It looks like a url, we should just return it as a url instead
        // of using the asset base url.
        if (assetId.startsWith('https://')) {
            return assetId;
        }
        // It would be nice to use new URL here, but its not
        // supported on IE. This will do for now.
        return `${baseURL}/${assetId}`;
    });
